import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, BedDouble, TrendingUp, TrendingDown, Trophy, Sparkles, Trash2, Pencil } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, ReferenceLine } from "recharts";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PERIODI = [
  { v: 7, label: "7gg" },
  { v: 30, label: "30gg" },
  { v: 90, label: "90gg" },
];

function Pill({ color, children }) {
  const m = {
    verde: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    giallo: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    rosso: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${m[color] || ""}`}>{children}</span>;
}

function semaforo(p, verde = 2.4, gialla = 2.0) {
  if (p >= verde) return "verde";
  if (p >= gialla) return "giallo";
  return "rosso";
}

export default function Housekeeping() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "housekeeping_manager", "resident_manager"].includes(user?.role);
  const isAdmin = user?.role === "super_admin";
  const [periodo, setPeriodo] = useState(30);
  const [dash, setDash] = useState(null);
  const [list, setList] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // row to delete
  const [deleting, setDeleting] = useState(false);
  const [editRow, setEditRow] = useState(null); // row being edited
  const [editForm, setEditForm] = useState({ data: "", camere_pulite: "", ore_lavorate: "", numero_risorse: 1, note: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  // form
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ hotel_id: "", data: today, camere_pulite: "", ore_lavorate: "", numero_risorse: 1, note: "" });

  const loadAll = async (p = periodo) => {
    setLoading(true);
    try {
      const today = new Date();
      const da = new Date(); da.setDate(today.getDate() - p);
      const [d, l, h, t] = await Promise.all([
        api.get(`/housekeeping/dashboard?periodo=${p}`),
        api.get(`/housekeeping/productivity?data_da=${da.toISOString().slice(0, 10)}&data_a=${today.toISOString().slice(0, 10)}`),
        api.get("/hotels"),
        api.get("/housekeeping/thresholds").catch(() => ({ data: {} })),
      ]);
      setDash(d.data);
      setList(l.data || []);
      setHotels(h.data || []);
      setThresholds(t.data || {});
      if (!form.hotel_id && h.data?.length) setForm((f) => ({ ...f, hotel_id: h.data[0].id }));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(periodo); }, [periodo]);

  // Soglie effettive per l'hotel selezionato nel form (fallback 2.4/2.0)
  const formHotelThr = thresholds[form.hotel_id] || { soglia_verde: 2.4, soglia_gialla: 2.0 };
  const rowThr = (hotelId) => thresholds[hotelId] || { soglia_verde: 2.4, soglia_gialla: 2.0 };

  const previewProd = (() => {
    const c = parseFloat(form.camere_pulite);
    const o = parseFloat(form.ore_lavorate);
    if (!c || !o) return null;
    return Math.round((c / o) * 100) / 100;  })();

  const nomeHotel = (id) => hotels.find((h) => h.id === id)?.nome || "—";

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/housekeeping/productivity/${confirmDelete.id}`);
      toast.success(`Riga eliminata · ${nomeHotel(confirmDelete.hotel_id)} ${confirmDelete.data}`);
      setConfirmDelete(null);
      await loadAll(periodo);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (row) => {
    setEditForm({
      data: row.data || "",
      camere_pulite: String(row.camere_pulite ?? ""),
      ore_lavorate: String(row.ore_lavorate ?? ""),
      numero_risorse: row.numero_risorse ?? 1,
      note: row.note || "",
    });
    setEditRow(row);
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const c = parseInt(editForm.camere_pulite);
    const o = parseFloat(editForm.ore_lavorate);
    if (!editForm.data || isNaN(c) || isNaN(o) || o <= 0) {
      toast.error("Compila data, camere e ore (ore > 0)");
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/housekeeping/productivity/${editRow.id}`, {
        data: editForm.data,
        camere_pulite: c,
        ore_lavorate: o,
        numero_risorse: parseInt(editForm.numero_risorse) || 1,
        note: (editForm.note || "").trim() || null,
      });
      toast.success("Produttività aggiornata");
      setEditRow(null);
      await loadAll(periodo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEdit(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canWrite) return;
    if (!form.hotel_id || !form.camere_pulite || !form.ore_lavorate) {
      toast.error("Compila tutti i campi");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/housekeeping/productivity", {
        hotel_id: form.hotel_id,
        data: form.data,
        camere_pulite: parseInt(form.camere_pulite),
        ore_lavorate: parseFloat(form.ore_lavorate),
        numero_risorse: parseInt(form.numero_risorse) || 1,
        note: (form.note || "").trim() || null,
      });
      toast.success("Produttività inserita");
      setForm({ ...form, camere_pulite: "", ore_lavorate: "", note: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="housekeeping-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Housekeeping</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Produttività · <span className="font-medium">camere / ora</span></h1>
        </div>
        <div className="flex gap-1 ih-glass rounded-full p-1">
          {PERIODI.map((p) => (
            <button key={p.v} data-testid={`hk-periodo-${p.v}`} onClick={() => setPeriodo(p.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${periodo === p.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="ih-glass rounded-2xl p-5" data-testid="kpi-media">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><BedDouble className="h-3 w-3" /> Produttività media</div>
            <div className="font-display text-3xl font-medium">{dash.media_produttivita}</div>
            <div className="text-[11px] text-muted-foreground mt-1">soglie per hotel · default ≥ 2.4</div>
          </div>
          <div className="ih-glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Distribuzione</div>
            <div className="flex gap-2 mt-1">
              <Pill color="verde">{dash.distribuzione_semaforo.verde} ottimo</Pill>
              <Pill color="giallo">{dash.distribuzione_semaforo.giallo} medio</Pill>
              <Pill color="rosso">{dash.distribuzione_semaforo.rosso} critico</Pill>
            </div>
          </div>
          <div className="ih-glass rounded-2xl p-5" data-testid="kpi-confronto">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1">{dash.confronto_settimane.delta_pct >= 0 ? <TrendingUp className="h-3 w-3 text-emerald-500" /> : <TrendingDown className="h-3 w-3 text-rose-500" />} Confronto settimane</div>
            <div className="font-display text-3xl font-medium">{dash.confronto_settimane.delta_pct >= 0 ? "+" : ""}{dash.confronto_settimane.delta_pct}%</div>
            <div className="text-[11px] text-muted-foreground mt-1">{dash.confronto_settimane.current} vs {dash.confronto_settimane.previous}</div>
          </div>
          <div className="ih-glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><Trophy className="h-3 w-3 text-yellow-500" /> Hotel top</div>
            <div className="font-display text-xl font-medium truncate">{dash.classifica_hotel[0]?.nome || "—"}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{dash.classifica_hotel[0]?.produttivita_media || "—"} camere/h</div>
          </div>
        </div>
      )}

      {/* Form inserimento */}
      {canWrite && (
        <form onSubmit={submit} className="ih-glass rounded-2xl p-6" data-testid="hk-form">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-lavender-500" />
            <h2 className="font-display text-lg tracking-tight">Inserisci giornata</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hotel</label>
              <select data-testid="form-hotel" value={form.hotel_id} onChange={(e) => setForm({ ...form, hotel_id: e.target.value })} className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm">
                {hotels.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data</label>
              <input type="date" data-testid="form-data" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Camere pulite</label>
              <input type="number" min="0" data-testid="form-camere" value={form.camere_pulite} onChange={(e) => setForm({ ...form, camere_pulite: e.target.value })} className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ore lavorate</label>
              <input type="number" step="0.5" min="0" data-testid="form-ore" value={form.ore_lavorate} onChange={(e) => setForm({ ...form, ore_lavorate: e.target.value })} className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Risorse</label>
              <input type="number" min="1" data-testid="form-risorse" value={form.numero_risorse} onChange={(e) => setForm({ ...form, numero_risorse: e.target.value })} className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Note (opzionale)</label>
            <textarea
              data-testid="hk-productivity-note-input"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              maxLength={500}
              rows={2}
              placeholder="Contesto: arrivi massicci, formazione, guasto lavatrice…"
              className="w-full mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none"
            />
            <div className="text-[10px] text-muted-foreground text-right mt-0.5">{(form.note || "").length}/500</div>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm" data-testid="form-preview">
              {previewProd != null ? (
                <span className="flex items-center gap-2">
                  Anteprima: <span className="font-display text-2xl">{previewProd}</span> camere/h
                  <Pill color={semaforo(previewProd, formHotelThr.soglia_verde, formHotelThr.soglia_gialla)}>
                    {semaforo(previewProd, formHotelThr.soglia_verde, formHotelThr.soglia_gialla)}
                  </Pill>
                  <span className="text-[10px] text-muted-foreground ml-1" data-testid="form-preview-thr">
                    (soglie: V {formHotelThr.soglia_verde} · G {formHotelThr.soglia_gialla})
                  </span>
                </span>
              ) : <span className="text-muted-foreground text-xs">Compila camere e ore per vedere l'anteprima…</span>}
            </div>
            <button type="submit" disabled={submitting} data-testid="hk-submit" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {submitting ? "Salvataggio…" : "Registra"}
            </button>
          </div>
        </form>
      )}

      {/* Trend settimanale */}
      {dash?.trend_settimanale?.length > 0 && (
        <div className="ih-glass rounded-2xl p-6">
          <h2 className="font-display text-lg tracking-tight mb-4">Trend settimanale</h2>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dash.trend_settimanale}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="settimana" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <ReferenceLine y={2.4} stroke="#10b981" strokeDasharray="3 3" label={{ value: "Target", fontSize: 10, fill: "#10b981" }} />
                <ReferenceLine y={2.0} stroke="#f59e0b" strokeDasharray="3 3" />
                <Bar dataKey="produttivita_media" fill="#c4a3e6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Classifica hotel */}
      {dash?.classifica_hotel?.length > 1 && (
        <div className="ih-glass rounded-2xl p-6" data-testid="classifica-hotel">
          <h2 className="font-display text-lg tracking-tight mb-1">Classifica hotel</h2>
          <p className="text-[11px] text-muted-foreground mb-4">Colori calcolati con le soglie specifiche di ogni struttura</p>
          <div className="space-y-2">
            {dash.classifica_hotel.map((h, i) => (
              <div key={h.hotel_id} className="rounded-xl border border-border/60 p-3 bg-background/40 flex items-center gap-3">
                <div className="text-xs font-bold w-6 text-muted-foreground">#{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{h.nome}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>{h.citta}</span>
                    <span className="text-muted-foreground/60">·</span>
                    <span
                      title="Soglie produttività di questo hotel"
                      data-testid={`row-thr-${h.hotel_id}`}
                      className="tabular-nums"
                    >
                      Soglie: <span className="text-emerald-600 dark:text-emerald-400">V ≥ {h.soglia_verde ?? 2.4}</span>
                      {" | "}
                      <span className="text-amber-600 dark:text-amber-400">G ≥ {h.soglia_gialla ?? 2.0}</span>
                    </span>
                  </div>
                </div>
                <div className="font-display text-xl">{h.produttivita_media}</div>
                <Pill color={h.semaforo}>{h.semaforo}</Pill>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Storico */}
      <div className="ih-glass rounded-2xl p-6">
        <h2 className="font-display text-lg tracking-tight mb-4">Storico inserimenti</h2>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : list.length === 0 ? <p className="text-sm text-muted-foreground">Nessun dato nel periodo selezionato.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hk-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border/60">
                  <th className="py-2 pr-2">Data</th>
                  <th className="py-2 pr-2">Hotel</th>
                  <th className="py-2 pr-2 text-right">Camere</th>
                  <th className="py-2 pr-2 text-right">Ore</th>
                  <th className="py-2 pr-2 text-right">Risorse</th>
                  <th className="py-2 pr-2 text-right">Produttività</th>
                  <th className="py-2 pr-2 text-right">Stato</th>
                  <th className="py-2 pr-2">Note</th>
                  {canWrite && <th className="py-2 pr-2 text-right w-20">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {list.slice(0, 50).map((r) => {
                  const thr = rowThr(r.hotel_id);
                  const canEditRow = isAdmin || (user?.id === r.inserito_da) || (
                    ["resident_manager", "housekeeping_manager"].includes(user?.role) &&
                    (user?.hotel_ids || []).includes(r.hotel_id)
                  );
                  return (
                  <tr key={r.id} className="border-b border-border/40" data-testid={`hk-row-${r.id}`}>
                    <td className="py-2 pr-2 text-muted-foreground">{r.data}</td>
                    <td className="py-2 pr-2">
                      {nomeHotel(r.hotel_id)}
                      <div className="text-[9px] text-muted-foreground tabular-nums">V {thr.soglia_verde} · G {thr.soglia_gialla}</div>
                    </td>
                    <td className="py-2 pr-2 text-right">{r.camere_pulite}</td>
                    <td className="py-2 pr-2 text-right">{r.ore_lavorate}</td>
                    <td className="py-2 pr-2 text-right">{r.numero_risorse}</td>
                    <td className="py-2 pr-2 text-right font-medium" title={r.note || undefined}>{r.produttivita}</td>
                    <td className="py-2 pr-2 text-right"><Pill color={r.semaforo}>{r.semaforo}</Pill></td>
                    <td className="py-2 pr-2 max-w-[220px]" data-testid={`hk-note-${r.id}`}>
                      {r.note ? (
                        <span className="text-xs text-muted-foreground line-clamp-2" title={r.note}>{r.note}</span>
                      ) : <span className="text-[11px] text-muted-foreground/60">—</span>}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-2 text-right whitespace-nowrap">
                        {canEditRow && (
                          <button onClick={() => openEdit(r)} title="Modifica riga" data-testid={`hk-productivity-edit-${r.id}`} className="text-muted-foreground hover:text-primary p-1 rounded transition-colors">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => setConfirmDelete(r)} title="Elimina riga" data-testid={`hk-delete-${r.id}`} className="text-rose-500 hover:text-rose-600 p-1 rounded transition-colors ml-1">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) setEditRow(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="hk-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Pencil className="h-4 w-4 text-primary" /> Modifica produttività
            </DialogTitle>
            <DialogDescription>
              <strong className="text-foreground">{nomeHotel(editRow?.hotel_id)}</strong> · aggiorna i dati e le note.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <label className="col-span-2 text-xs">
              <span className="block text-muted-foreground mb-1">Data</span>
              <input
                type="date"
                value={editForm.data}
                onChange={(e) => setEditForm({ ...editForm, data: e.target.value })}
                className="w-full ih-glass rounded-lg px-3 py-2 text-sm"
                data-testid="hk-edit-data"
              />
            </label>
            <label className="text-xs">
              <span className="block text-muted-foreground mb-1">Camere pulite</span>
              <input
                type="number"
                min="0"
                value={editForm.camere_pulite}
                onChange={(e) => setEditForm({ ...editForm, camere_pulite: e.target.value })}
                className="w-full ih-glass rounded-lg px-3 py-2 text-sm tabular-nums"
                data-testid="hk-edit-camere"
              />
            </label>
            <label className="text-xs">
              <span className="block text-muted-foreground mb-1">Ore lavorate</span>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={editForm.ore_lavorate}
                onChange={(e) => setEditForm({ ...editForm, ore_lavorate: e.target.value })}
                className="w-full ih-glass rounded-lg px-3 py-2 text-sm tabular-nums"
                data-testid="hk-edit-ore"
              />
            </label>
            <label className="col-span-2 text-xs">
              <span className="block text-muted-foreground mb-1">Numero risorse</span>
              <input
                type="number"
                min="1"
                value={editForm.numero_risorse}
                onChange={(e) => setEditForm({ ...editForm, numero_risorse: e.target.value })}
                className="w-full ih-glass rounded-lg px-3 py-2 text-sm tabular-nums"
                data-testid="hk-edit-risorse"
              />
            </label>
            <label className="col-span-2 text-xs">
              <span className="block text-muted-foreground mb-1">Note (facoltative, max 500)</span>
              <textarea
                rows={3}
                maxLength={500}
                value={editForm.note}
                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                className="w-full ih-glass rounded-lg px-3 py-2 text-sm resize-none"
                placeholder="Es. extra ospiti, personale ridotto, pulizia profonda…"
                data-testid="hk-edit-note"
              />
              <div className="text-[10px] text-muted-foreground mt-1 text-right tabular-nums">{(editForm.note || "").length}/500</div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} disabled={savingEdit}>Annulla</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="hk-edit-save-btn">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Pencil className="h-4 w-4 mr-1" />}
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="hk-confirm-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-rose-500" /> Elimina riga produttività
            </DialogTitle>
            <DialogDescription>
              Struttura <strong className="text-foreground">{nomeHotel(confirmDelete?.hotel_id)}</strong> · Settimana <strong className="text-foreground tabular-nums">{confirmDelete?.data}</strong> · <strong className="text-foreground">{confirmDelete?.camere_pulite}</strong> camere in <strong className="text-foreground">{confirmDelete?.ore_lavorate}</strong> ore. <span className="text-rose-600">L'azione è irreversibile.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annulla</Button>
            <Button onClick={doDelete} disabled={deleting} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="hk-confirm-delete-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
